import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Eye } from 'lucide-react';
import { adAPI, domainAPI } from '../../services/api';
import toast from 'react-hot-toast';
import '../Admin/Admin.css';

const AD_POSITIONS = [
  { value: 'head_code', label: 'Head Code (Scripts in <head>)', group: 'Global' },
  { value: 'below_header', label: 'Below Header (All Pages)', group: 'Global' },
  { value: 'footer', label: 'Footer (All Pages)', group: 'Global' },
  { value: 'floating', label: 'Floating (Corner)', group: 'Global' },
  { value: 'home_banner', label: 'Home Banner', group: 'Home Page' },
  { value: 'home_middle', label: 'Home Middle', group: 'Home Page' },
  { value: 'home_bottom', label: 'Home Bottom', group: 'Home Page' },
  { value: 'category_top', label: 'Category Top', group: 'Category Page' },
  { value: 'category_middle', label: 'Category Middle', group: 'Category Page' },
  { value: 'novel_top', label: 'Novel Detail Top', group: 'Novel Page' },
  { value: 'novel_middle', label: 'Novel Detail Middle', group: 'Novel Page' },
  { value: 'novel_bottom', label: 'Novel Detail Bottom', group: 'Novel Page' },
  { value: 'sidebar', label: 'Sidebar', group: 'Novel Page' },
  { value: 'chapter_top', label: 'Chapter Top', group: 'Reading Page' },
  { value: 'chapter_middle', label: 'Chapter Middle', group: 'Reading Page' },
  { value: 'chapter_bottom', label: 'Chapter Bottom', group: 'Reading Page' },
  { value: 'chapter_between', label: 'Between Paragraphs (Custom)', group: 'Reading Page' }
];

const AD_TYPES = [
  { value: 'custom_code', label: '📝 Custom Code (HTML/JS)', description: 'Paste any ad code - auto detects HTML & JavaScript' },
  { value: 'adsense', label: '🔵 Google AdSense', description: 'Google AdSense display ads' },
  { value: 'image_banner', label: '🖼️ Image Banner', description: 'Simple image with click link' },
  { value: 'gam', label: '🟢 Google Ad Manager', description: 'DFP/GAM ad units' },
  { value: 'video', label: '🎬 Video Ad', description: 'Video advertisement' }
];

const AdForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    position: 'below_header',
    adType: 'custom_code',
    // Main code field - THIS IS THE IMPORTANT ONE
    code: '',
    // AdSense
    adsenseConfig: {
      adClient: '',
      adSlot: '',
      adFormat: 'auto',
      fullWidthResponsive: true,
      adStyle: ''
    },
    // GAM
    gamConfig: {
      adUnitPath: '',
      size: '[[300,250], [728,90]]',
      sizeMapping: '',
      targeting: '',
      lazyLoad: true,
      refresh: {
        enabled: false,
        interval: 30
      }
    },
    // Image Banner
    imageBanner: {
      imageUrl: '',
      imageUrlMobile: '',
      linkUrl: '',
      altText: '',
      openInNewTab: true,
      nofollow: true
    },
    // Video
    videoConfig: {
      videoUrl: '',
      posterUrl: '',
      autoplay: false,
      muted: true,
      loop: false,
      clickUrl: ''
    },
    // Display
    displaySettings: {
      showOnDesktop: true,
      showOnMobile: true,
      width: '100%',
      height: 'auto',
      maxWidth: '728px',
      minHeight: '',
      backgroundColor: '',
      padding: '10px 0',
      margin: '0 auto',
      borderRadius: '',
      border: '',
      boxShadow: '',
      customClass: '',
      customStyle: '',
      textAlign: 'center',
      showLabel: false,
      labelText: 'Advertisement'
    },
    // Chapter
    chapterSettings: {
      paragraphMode: 'fixed',       // 'fixed' = chỉ 1 lần, 'auto' = lặp lại
      paragraphNumber: 5,           // số đoạn
      insertAfterParagraphs: 5,
      maxAdsPerChapter: 3,
      minParagraphsRequired: 10
    },
    // Popup
    popupSettings: {
      showDelay: 0,
      showOncePerSession: true,
      showOncePerDay: false,
      closeButtonDelay: 0,
      overlayOpacity: 0.7
    },
    // Floating
    floatingSettings: {
      position: 'bottom-right',
      offsetX: 20,
      offsetY: 20,
      showCloseButton: true
    },
    priority: 0,
    isActive: true,
    notes: ''
  });

  useEffect(() => {
    fetchDomains();
    if (isEdit) {
      fetchAd();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDomains = async () => {
    try {
      const response = await domainAPI.getAll();
      setDomains(response.data || []);
      if (!isEdit && response.data?.length > 0) {
        setFormData(prev => ({ ...prev, domain: response.data[0]._id }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAd = async () => {
    try {
      const response = await adAPI.getById(id);
      const ad = response.data;
      
      console.log('Loaded ad data:', ad); // Debug log
      
      // Map old chapterSettings to new fields
      const loadedChapterSettings = ad.chapterSettings || {};
      const chapterSettings = {
        paragraphMode: loadedChapterSettings.paragraphMode || 'fixed',
        paragraphNumber: loadedChapterSettings.paragraphNumber || loadedChapterSettings.insertAfterParagraphs || 5,
        insertAfterParagraphs: loadedChapterSettings.insertAfterParagraphs || 5,
        maxAdsPerChapter: loadedChapterSettings.maxAdsPerChapter || 3,
        minParagraphsRequired: loadedChapterSettings.minParagraphsRequired || 10
      };
      
      setFormData(prev => ({
        ...prev,
        name: ad.name || '',
        domain: ad.domain?._id || ad.domain || '',
        position: ad.position || 'below_header',
        adType: ad.adType || 'custom_code',
        // Load code field - check multiple possible field names
        code: ad.code || ad.customCode || ad.htmlCode || ad.jsCode || '',
        adsenseConfig: { ...prev.adsenseConfig, ...ad.adsenseConfig },
        gamConfig: { ...prev.gamConfig, ...ad.gamConfig },
        imageBanner: { ...prev.imageBanner, ...ad.imageBanner },
        videoConfig: { ...prev.videoConfig, ...ad.videoConfig },
        displaySettings: { ...prev.displaySettings, ...ad.displaySettings },
        chapterSettings: chapterSettings,
        popupSettings: { ...prev.popupSettings, ...ad.popupSettings },
        floatingSettings: { ...prev.floatingSettings, ...ad.floatingSettings },
        priority: ad.priority || 0,
        isActive: ad.isActive !== false,
        notes: ad.notes || ''
      }));
    } catch (error) {
      toast.error('Failed to load ad');
      navigate('/admin/ads');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter ad name');
      return;
    }
    if (!formData.domain) {
      toast.error('Please select a domain');
      return;
    }

    setIsLoading(true);
    try {
      // Prepare payload - ensure code is included
      const payload = {
        ...formData,
        // Always send code field
        code: formData.code || '',
      };
      
      console.log('Submitting payload:', payload); // Debug log

      if (isEdit) {
        await adAPI.update(id, payload);
        toast.success('Ad updated successfully');
      } else {
        await adAPI.create(payload);
        toast.success('Ad created successfully');
      }
      navigate('/admin/ads');
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedFormData = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const renderAdPreview = () => {
    const { adType } = formData;
    
    if (adType === 'adsense') {
      return (
        <div style={{ padding: '40px', background: '#f0f0f0', textAlign: 'center', borderRadius: '8px' }}>
          <p style={{ color: '#4285f4', fontWeight: 'bold' }}>Google AdSense</p>
          <p style={{ fontSize: '0.8rem', color: '#666' }}>
            Client: {formData.adsenseConfig.adClient || 'Not set'}<br/>
            Slot: {formData.adsenseConfig.adSlot || 'Not set'}
          </p>
        </div>
      );
    }
    
    if (adType === 'gam') {
      return (
        <div style={{ padding: '40px', background: '#e8f5e9', textAlign: 'center', borderRadius: '8px' }}>
          <p style={{ color: '#34a853', fontWeight: 'bold' }}>Google Ad Manager</p>
          <p style={{ fontSize: '0.8rem', color: '#666' }}>
            Unit: {formData.gamConfig.adUnitPath || 'Not set'}<br/>
            Size: {formData.gamConfig.size || 'Not set'}
          </p>
        </div>
      );
    }
    
    if (adType === 'image_banner') {
      return (
        <a href={formData.imageBanner.linkUrl || '#'} target="_blank" rel="noopener noreferrer">
          <img 
            src={formData.imageBanner.imageUrl || 'https://via.placeholder.com/728x90?text=Banner+Preview'} 
            alt={formData.imageBanner.altText || 'Ad'}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </a>
      );
    }
    
    if (adType === 'custom_code') {
      return (
        <div style={{ padding: '20px', background: '#1a1a2e', borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.8rem' }}>
          <p style={{ color: '#4ade80', marginBottom: '10px' }}>📝 Custom Code Preview:</p>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#e2e8f0', maxHeight: '200px', overflow: 'auto' }}>
            {formData.code || '// No code yet'}
          </pre>
        </div>
      );
    }
    
    return <p style={{ color: '#999', textAlign: 'center' }}>Preview not available for this ad type</p>;
  };

  if (isFetching) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const groupedPositions = AD_POSITIONS.reduce((acc, pos) => {
    if (!acc[pos.group]) acc[pos.group] = [];
    acc[pos.group].push(pos);
    return acc;
  }, {});

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'code', label: 'Ad Code' },
    { id: 'display', label: 'Display Settings' },
    { id: 'advanced', label: 'Advanced' }
  ];

  return (
    <div className="ad-form-page">
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button 
            onClick={() => navigate('/admin/ads')}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1>{isEdit ? 'Edit Ad' : 'Create New Ad'}</h1>
        </div>
        <button type="button" className="btn-add" onClick={() => setShowPreview(!showPreview)}>
          <Eye size={18} />
          {showPreview ? 'Hide Preview' : 'Show Preview'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: showPreview ? '1fr 400px' : '1fr', gap: '24px' }}>
        <form onSubmit={handleSubmit} className="admin-form" style={{ maxWidth: '100%' }}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 20px',
                  background: activeTab === tab.id ? 'var(--primary)' : 'transparent',
                  color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label>Ad Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => updateFormData('name', e.target.value)}
                    placeholder="e.g., Header Banner - Main Site"
                  />
                </div>
                <div className="form-group">
                  <label>Domain *</label>
                  <select value={formData.domain} onChange={(e) => updateFormData('domain', e.target.value)}>
                    <option value="">-- Select Domain --</option>
                    {domains.map((d) => (
                      <option key={d._id} value={d._id}>{d.domain} - {d.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Position *</label>
                  <select value={formData.position} onChange={(e) => updateFormData('position', e.target.value)}>
                    {Object.entries(groupedPositions).map(([group, positions]) => (
                      <optgroup key={group} label={group}>
                        {positions.map((pos) => (
                          <option key={pos.value} value={pos.value}>{pos.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Ad Type *</label>
                  <select value={formData.adType} onChange={(e) => updateFormData('adType', e.target.value)}>
                    {AD_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Paragraph settings for chapter_between */}
              {formData.position === 'chapter_between' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Mode *</label>
                      <select
                        value={formData.chapterSettings.paragraphMode || 'fixed'}
                        onChange={(e) => updateNestedFormData('chapterSettings', 'paragraphMode', e.target.value)}
                      >
                        <option value="fixed">Fixed (hiện 1 lần tại vị trí cố định)</option>
                        <option value="auto">Auto (lặp lại sau mỗi X đoạn)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Paragraph Number *</label>
                      <input
                        type="number"
                        value={formData.chapterSettings.paragraphNumber || 5}
                        onChange={(e) => updateNestedFormData('chapterSettings', 'paragraphNumber', parseInt(e.target.value) || 5)}
                        min="1"
                        placeholder="5"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <small style={{ color: 'var(--text-muted)', display: 'block', marginTop: '-10px', marginBottom: '15px' }}>
                      {formData.chapterSettings.paragraphMode === 'auto' 
                        ? `✓ Auto ${formData.chapterSettings.paragraphNumber || 5}: Hiện sau đoạn ${formData.chapterSettings.paragraphNumber || 5}, ${(formData.chapterSettings.paragraphNumber || 5) * 2}, ${(formData.chapterSettings.paragraphNumber || 5) * 3}...`
                        : `✓ Fixed ${formData.chapterSettings.paragraphNumber || 5}: Chỉ hiện 1 lần sau đoạn ${formData.chapterSettings.paragraphNumber || 5}`
                      }
                    </small>
                  </div>
                </>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Priority (higher = shows first)</label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => updateFormData('priority', parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-item" style={{ width: 'fit-content', marginTop: '24px' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => updateFormData('isActive', e.target.checked)}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Notes (internal)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => updateFormData('notes', e.target.value)}
                  placeholder="Internal notes about this ad..."
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Ad Code Tab */}
          {activeTab === 'code' && (
            <div className="form-section">
              {/* AdSense */}
              {formData.adType === 'adsense' && (
                <>
                  <h3>Google AdSense Configuration</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Ad Client (ca-pub-xxx)</label>
                      <input
                        type="text"
                        value={formData.adsenseConfig.adClient}
                        onChange={(e) => updateNestedFormData('adsenseConfig', 'adClient', e.target.value)}
                        placeholder="ca-pub-XXXXXXXXXXXXXXXX"
                      />
                    </div>
                    <div className="form-group">
                      <label>Ad Slot</label>
                      <input
                        type="text"
                        value={formData.adsenseConfig.adSlot}
                        onChange={(e) => updateNestedFormData('adsenseConfig', 'adSlot', e.target.value)}
                        placeholder="XXXXXXXXXX"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Format</label>
                      <select
                        value={formData.adsenseConfig.adFormat}
                        onChange={(e) => updateNestedFormData('adsenseConfig', 'adFormat', e.target.value)}
                      >
                        <option value="auto">Auto</option>
                        <option value="horizontal">Horizontal</option>
                        <option value="vertical">Vertical</option>
                        <option value="rectangle">Rectangle</option>
                        <option value="fluid">Fluid</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="checkbox-item" style={{ width: 'fit-content', marginTop: '24px' }}>
                        <input
                          type="checkbox"
                          checked={formData.adsenseConfig.fullWidthResponsive}
                          onChange={(e) => updateNestedFormData('adsenseConfig', 'fullWidthResponsive', e.target.checked)}
                        />
                        <span>Full Width Responsive</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* GAM */}
              {formData.adType === 'gam' && (
                <>
                  <h3>Google Ad Manager Configuration</h3>
                  <div className="form-group">
                    <label>Ad Unit Path</label>
                    <input
                      type="text"
                      value={formData.gamConfig.adUnitPath}
                      onChange={(e) => updateNestedFormData('gamConfig', 'adUnitPath', e.target.value)}
                      placeholder="/12345678/homepage_banner"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Size (JSON array)</label>
                      <input
                        type="text"
                        value={formData.gamConfig.size}
                        onChange={(e) => updateNestedFormData('gamConfig', 'size', e.target.value)}
                        placeholder="[[300,250], [336,280]]"
                      />
                    </div>
                    <div className="form-group">
                      <label className="checkbox-item" style={{ width: 'fit-content', marginTop: '24px' }}>
                        <input
                          type="checkbox"
                          checked={formData.gamConfig.lazyLoad}
                          onChange={(e) => updateNestedFormData('gamConfig', 'lazyLoad', e.target.checked)}
                        />
                        <span>Lazy Load</span>
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Targeting (JSON)</label>
                    <textarea
                      value={formData.gamConfig.targeting}
                      onChange={(e) => updateNestedFormData('gamConfig', 'targeting', e.target.value)}
                      placeholder='{"key": "value"}'
                      rows={3}
                    />
                  </div>
                </>
              )}

              {/* Custom Code - THE MAIN ONE */}
              {formData.adType === 'custom_code' && (
                <>
                  <h3>📝 Custom Ad Code</h3>
                  <p style={{ color: 'var(--text-muted)', marginBottom: '16px', fontSize: '0.9rem' }}>
                    Paste any ad code here - supports HTML, JavaScript, or mixed content. 
                    Script tags will be automatically executed.
                  </p>
                  <div className="form-group">
                    <label>Ad Code *</label>
                    <textarea
                      value={formData.code}
                      onChange={(e) => updateFormData('code', e.target.value)}
                      placeholder={`<!-- Paste your ad code here -->
<script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"></script>
<script>
  window.googletag = window.googletag || {cmd: []};
  googletag.cmd.push(function() {
    googletag.defineSlot('/12345/ad-unit', [728, 90], 'div-ad').addService(googletag.pubads());
    googletag.enableServices();
  });
</script>
<div id="div-ad">
  <script>googletag.cmd.push(function() { googletag.display('div-ad'); });</script>
</div>`}
                      rows={20}
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: '1.5' }}
                    />
                    <small style={{ color: 'var(--text-muted)' }}>
                      Supports: Google Ad Manager, AdSense, affiliate codes, custom banners, tracking scripts, etc.
                    </small>
                  </div>
                </>
              )}

              {/* Image Banner */}
              {formData.adType === 'image_banner' && (
                <>
                  <h3>Image Banner Configuration</h3>
                  <div className="form-group">
                    <label>Image URL (Desktop)</label>
                    <input
                      type="text"
                      value={formData.imageBanner.imageUrl}
                      onChange={(e) => updateNestedFormData('imageBanner', 'imageUrl', e.target.value)}
                      placeholder="https://example.com/banner.jpg"
                    />
                  </div>
                  <div className="form-group">
                    <label>Image URL (Mobile) - Optional</label>
                    <input
                      type="text"
                      value={formData.imageBanner.imageUrlMobile}
                      onChange={(e) => updateNestedFormData('imageBanner', 'imageUrlMobile', e.target.value)}
                      placeholder="https://example.com/banner-mobile.jpg"
                    />
                  </div>
                  <div className="form-group">
                    <label>Click URL</label>
                    <input
                      type="text"
                      value={formData.imageBanner.linkUrl}
                      onChange={(e) => updateNestedFormData('imageBanner', 'linkUrl', e.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Alt Text</label>
                      <input
                        type="text"
                        value={formData.imageBanner.altText}
                        onChange={(e) => updateNestedFormData('imageBanner', 'altText', e.target.value)}
                        placeholder="Banner description"
                      />
                    </div>
                    <div className="form-group" style={{ display: 'flex', gap: '20px', marginTop: '24px' }}>
                      <label className="checkbox-item" style={{ width: 'fit-content' }}>
                        <input
                          type="checkbox"
                          checked={formData.imageBanner.openInNewTab}
                          onChange={(e) => updateNestedFormData('imageBanner', 'openInNewTab', e.target.checked)}
                        />
                        <span>Open in new tab</span>
                      </label>
                      <label className="checkbox-item" style={{ width: 'fit-content' }}>
                        <input
                          type="checkbox"
                          checked={formData.imageBanner.nofollow}
                          onChange={(e) => updateNestedFormData('imageBanner', 'nofollow', e.target.checked)}
                        />
                        <span>Nofollow</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* Video */}
              {formData.adType === 'video' && (
                <>
                  <h3>Video Ad Configuration</h3>
                  <div className="form-group">
                    <label>Video URL</label>
                    <input
                      type="text"
                      value={formData.videoConfig.videoUrl}
                      onChange={(e) => updateNestedFormData('videoConfig', 'videoUrl', e.target.value)}
                      placeholder="https://example.com/video.mp4"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Poster Image URL</label>
                      <input
                        type="text"
                        value={formData.videoConfig.posterUrl}
                        onChange={(e) => updateNestedFormData('videoConfig', 'posterUrl', e.target.value)}
                        placeholder="https://example.com/poster.jpg"
                      />
                    </div>
                    <div className="form-group">
                      <label>Click URL</label>
                      <input
                        type="text"
                        value={formData.videoConfig.clickUrl}
                        onChange={(e) => updateNestedFormData('videoConfig', 'clickUrl', e.target.value)}
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>
                  <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.videoConfig.autoplay}
                        onChange={(e) => updateNestedFormData('videoConfig', 'autoplay', e.target.checked)}
                      />
                      <span>Autoplay</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.videoConfig.muted}
                        onChange={(e) => updateNestedFormData('videoConfig', 'muted', e.target.checked)}
                      />
                      <span>Muted</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.videoConfig.loop}
                        onChange={(e) => updateNestedFormData('videoConfig', 'loop', e.target.checked)}
                      />
                      <span>Loop</span>
                    </label>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Display Settings Tab */}
          {activeTab === 'display' && (
            <div className="form-section">
              <h3>Device Visibility</h3>
              <div className="form-row">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.displaySettings.showOnDesktop}
                    onChange={(e) => updateNestedFormData('displaySettings', 'showOnDesktop', e.target.checked)}
                  />
                  <span>🖥️ Show on Desktop</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.displaySettings.showOnMobile}
                    onChange={(e) => updateNestedFormData('displaySettings', 'showOnMobile', e.target.checked)}
                  />
                  <span>📱 Show on Mobile</span>
                </label>
              </div>

              <h3 style={{ marginTop: '24px' }}>Dimensions & Style</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Width</label>
                  <input
                    type="text"
                    value={formData.displaySettings.width}
                    onChange={(e) => updateNestedFormData('displaySettings', 'width', e.target.value)}
                    placeholder="100%, 728px, auto"
                  />
                </div>
                <div className="form-group">
                  <label>Max Width</label>
                  <input
                    type="text"
                    value={formData.displaySettings.maxWidth}
                    onChange={(e) => updateNestedFormData('displaySettings', 'maxWidth', e.target.value)}
                    placeholder="728px"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Height</label>
                  <input
                    type="text"
                    value={formData.displaySettings.height}
                    onChange={(e) => updateNestedFormData('displaySettings', 'height', e.target.value)}
                    placeholder="auto, 90px"
                  />
                </div>
                <div className="form-group">
                  <label>Min Height</label>
                  <input
                    type="text"
                    value={formData.displaySettings.minHeight}
                    onChange={(e) => updateNestedFormData('displaySettings', 'minHeight', e.target.value)}
                    placeholder="50px"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Padding</label>
                  <input
                    type="text"
                    value={formData.displaySettings.padding}
                    onChange={(e) => updateNestedFormData('displaySettings', 'padding', e.target.value)}
                    placeholder="10px 0"
                  />
                </div>
                <div className="form-group">
                  <label>Margin</label>
                  <input
                    type="text"
                    value={formData.displaySettings.margin}
                    onChange={(e) => updateNestedFormData('displaySettings', 'margin', e.target.value)}
                    placeholder="0 auto"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Background Color</label>
                  <input
                    type="text"
                    value={formData.displaySettings.backgroundColor}
                    onChange={(e) => updateNestedFormData('displaySettings', 'backgroundColor', e.target.value)}
                    placeholder="transparent, #fff"
                  />
                </div>
                <div className="form-group">
                  <label>Text Align</label>
                  <select
                    value={formData.displaySettings.textAlign}
                    onChange={(e) => updateNestedFormData('displaySettings', 'textAlign', e.target.value)}
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>

              <h3 style={{ marginTop: '24px' }}>Ad Label</h3>
              <div className="form-row">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.displaySettings.showLabel}
                    onChange={(e) => updateNestedFormData('displaySettings', 'showLabel', e.target.checked)}
                  />
                  <span>Show "Advertisement" label</span>
                </label>
                {formData.displaySettings.showLabel && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <input
                      type="text"
                      value={formData.displaySettings.labelText}
                      onChange={(e) => updateNestedFormData('displaySettings', 'labelText', e.target.value)}
                      placeholder="Advertisement"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="form-section">
              {/* Chapter Settings */}
              {formData.position === 'chapter_between' && (
                <>
                  <h3>Chapter Settings</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Insert after every X paragraphs</label>
                      <input
                        type="number"
                        value={formData.chapterSettings.insertAfterParagraphs}
                        onChange={(e) => updateNestedFormData('chapterSettings', 'insertAfterParagraphs', parseInt(e.target.value) || 5)}
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Max ads per chapter</label>
                      <input
                        type="number"
                        value={formData.chapterSettings.maxAdsPerChapter}
                        onChange={(e) => updateNestedFormData('chapterSettings', 'maxAdsPerChapter', parseInt(e.target.value) || 3)}
                        min="1"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Min paragraphs required to show ads</label>
                    <input
                      type="number"
                      value={formData.chapterSettings.minParagraphsRequired}
                      onChange={(e) => updateNestedFormData('chapterSettings', 'minParagraphsRequired', parseInt(e.target.value) || 10)}
                      min="1"
                    />
                  </div>
                </>
              )}

              {/* Popup Settings */}
              {formData.position === 'popup' && (
                <>
                  <h3>Popup Settings</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Show delay (seconds)</label>
                      <input
                        type="number"
                        value={formData.popupSettings.showDelay}
                        onChange={(e) => updateNestedFormData('popupSettings', 'showDelay', parseInt(e.target.value) || 0)}
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>Close button delay (seconds)</label>
                      <input
                        type="number"
                        value={formData.popupSettings.closeButtonDelay}
                        onChange={(e) => updateNestedFormData('popupSettings', 'closeButtonDelay', parseInt(e.target.value) || 0)}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.popupSettings.showOncePerSession}
                        onChange={(e) => updateNestedFormData('popupSettings', 'showOncePerSession', e.target.checked)}
                      />
                      <span>Once per session</span>
                    </label>
                    <label className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.popupSettings.showOncePerDay}
                        onChange={(e) => updateNestedFormData('popupSettings', 'showOncePerDay', e.target.checked)}
                      />
                      <span>Once per day</span>
                    </label>
                  </div>
                </>
              )}

              {/* Floating Settings */}
              {formData.position === 'floating' && (
                <>
                  <h3>Floating Settings</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Position</label>
                      <select
                        value={formData.floatingSettings.position}
                        onChange={(e) => updateNestedFormData('floatingSettings', 'position', e.target.value)}
                      >
                        <option value="top-left">Top Left</option>
                        <option value="top-right">Top Right</option>
                        <option value="bottom-left">Bottom Left</option>
                        <option value="bottom-right">Bottom Right</option>
                        <option value="bottom-center">Bottom Center</option>
                      </select>
                    </div>
                    <label className="checkbox-item" style={{ marginTop: '24px' }}>
                      <input
                        type="checkbox"
                        checked={formData.floatingSettings.showCloseButton}
                        onChange={(e) => updateNestedFormData('floatingSettings', 'showCloseButton', e.target.checked)}
                      />
                      <span>Show close button</span>
                    </label>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Offset X (px)</label>
                      <input
                        type="number"
                        value={formData.floatingSettings.offsetX}
                        onChange={(e) => updateNestedFormData('floatingSettings', 'offsetX', parseInt(e.target.value) || 20)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Offset Y (px)</label>
                      <input
                        type="number"
                        value={formData.floatingSettings.offsetY}
                        onChange={(e) => updateNestedFormData('floatingSettings', 'offsetY', parseInt(e.target.value) || 20)}
                      />
                    </div>
                  </div>
                </>
              )}

              <h3 style={{ marginTop: '24px' }}>Custom Styling</h3>
              <div className="form-group">
                <label>Custom CSS Class</label>
                <input
                  type="text"
                  value={formData.displaySettings.customClass}
                  onChange={(e) => updateNestedFormData('displaySettings', 'customClass', e.target.value)}
                  placeholder="my-custom-ad"
                />
              </div>
              <div className="form-group">
                <label>Custom Inline Style</label>
                <textarea
                  value={formData.displaySettings.customStyle}
                  onChange={(e) => updateNestedFormData('displaySettings', 'customStyle', e.target.value)}
                  placeholder="border: 1px solid #ccc; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
                  rows={3}
                />
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : (
                <>
                  <Save size={18} />
                  {isEdit ? 'Update Ad' : 'Create Ad'}
                </>
              )}
            </button>
            <button type="button" className="btn-cancel" onClick={() => navigate('/admin/ads')}>
              Cancel
            </button>
          </div>
        </form>

        {/* Preview */}
        {showPreview && (
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: 'var(--radius-md)',
            padding: '20px',
            border: '1px solid var(--border)',
            position: 'sticky',
            top: '88px',
            height: 'fit-content'
          }}>
            <h3 style={{ marginBottom: '16px', fontSize: '1rem' }}>Preview</h3>
            <div style={{
              background: 'white',
              padding: '10px',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              {renderAdPreview()}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .form-section h3 {
          font-size: 1rem;
          color: var(--primary);
          margin-bottom: 16px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }
        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AdForm;